import {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CircleDollarSign,
  HammerIcon,
  ListChecksIcon,
  Truck,
} from 'lucide-react';

import ErrorBoundary from 'components/ErrorBoundary';
import { isBuiltLoadBuildingSuggestion } from 'components/Kit/BuiltLoads/utils/isBuiltLoadBuildingSuggestion';
import KitEmailSuggestions from 'components/Kit/EmailSuggestions/KitEmailSuggestions';
import LoadCloneRecommendationCard from 'components/Kit/EmailSuggestions/components/LoadCloneRecommendationCard';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TabsTriggerVariants,
} from 'components/Tabs';
import { Toaster } from 'components/ToasterProvider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import {
  QuoteSidebarPortName,
  UpdateLoadBuildingSuggestionDataAction,
  UpdateQuoteRequestDataAction,
} from 'constants/BackgroundScript';
import { useLoadSearch } from 'contexts/loadSearchContext';
import {
  ServiceFeaturesListType,
  isQuickQuoteUiEnabled,
} from 'contexts/serviceContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { SidebarViewContext } from 'contexts/sidebarViewContext';
import { useIsTqlInbox } from 'hooks/useIsTqlInbox';
import {
  KitThreadCheckPhase,
  isKitThreadCheckAvailable,
  useKitThreadCheck,
} from 'hooks/useKitThreadCheck';
import { useLatestEmailProcessing } from 'hooks/useLatestEmailProcessing';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useThreadScopedState } from 'hooks/useThreadScopedState';
import {
  CloneJobStatus,
  LoadCloneRecommendation,
  LoadCloneRecommendationDisposition,
  getLoadCloneJob,
  getLoadCloneRecommendations,
  isFinishedCloneJobStatus,
} from 'lib/api/cloneLoads';
import {
  CreateLoadBuildingSuggestionRequest,
  createLoadBuildingSuggestion,
} from 'lib/api/createLoadBuildingSuggestion';
import {
  CreateQuoteRequestSuggestionRequest,
  createQuoteRequestSuggestion,
} from 'lib/api/createQuoteRequestSuggestion';
import { getLoadBuildingSuggestion } from 'lib/api/getLoadBuildingSuggestion';
import { getQuickQuoteSuggestion } from 'lib/api/getQuickQuoteSuggestion';
import { getQuoteRequest } from 'lib/api/getQuoteRequest';
import getTruckList, { TruckListResponse } from 'lib/api/getTruckList';
import { KitThreadCheckKind } from 'lib/api/kitThreadCheck';
import { Email } from 'types/Email';
import { QuoteRequest } from 'types/QuoteRequest';
import { AvailableTabs } from 'types/QuoteView';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { SidepanelMessage } from 'types/chromescript/util';
import SidebarView from 'types/enums/SidebarView';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';
import { resolveClickedSuggestionForPipelineTab } from 'utils/completeKitSuggestion';
import { hasQuoteViewSuggestions } from 'utils/hasQuoteViewSuggestions';
import { isLoadBuildingSuggestionActionable } from 'utils/isLoadBuildingSuggestionActionable';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';
import { normalizeSuggestions } from 'utils/normalizeTimezone';
import { filterInvalidSuggestions } from 'utils/suggestionValidators';
import { resolveTransporeonMissingStates } from 'utils/transporeonHelpers';

import LoadBuildingTab from './LoadBuilding/LoadBuildingTab';
import ExcelSharepointLoadBuildingHost from './LoadBuilding/sharepoint/components/ExcelSharepointLoadBuildingHost';
import { useSharepointLoadBuildingProcessing } from './LoadBuilding/sharepoint/providers/SharepointLoadBuildingProcessingProvider';
import {
  areCarouselSuggestionListsEquivalent,
  getCarouselSuggestionStabilityKey,
} from './LoadBuilding/sharepoint/utils/sharepointSuggestions';
import CarrierQuoteTab from './Quoting/CarrierQuote/CarrierQuoteTab';
import QuickQuoteTab from './Quoting/RequestQuickQuote/QuickQuoteTab';
import TruckListTab from './TruckList/TruckListTab';
import {
  buildLocalLoadBuildingSuggestionFromRequest,
  buildLocalQuoteSuggestionFromRequest,
  withSuggestedCustomerName,
} from './scrapedSuggestionUtil';

/** Must render under {@link ExcelSharepointLoadBuildingHost} (SharePoint processing context). */
function LoadBuildingTabSharepointSpinner() {
  const { isProcessing } = useSharepointLoadBuildingProcessing();

  if (!isProcessing) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger
          type='button'
          className='inline-flex shrink-0 cursor-default'
          aria-label='SharePoint loads processing'
        >
          <ButtonLoader className='mr-0 h-3.5 w-3.5 text-neutral-500' />
        </TooltipTrigger>
        <TooltipContent side='top'>SharePoint loads processing</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Get the default tab based on the hierarchy of service features enabled.
 * Note that this is independent and before setting the *initially displayed* tab based on loaded suggestions.
 *
 * @param features - The service features enabled.
 * @returns The default tab.
 */
const setDefaultTabByFeatureFlagsHierarchy = (
  features: ServiceFeaturesListType
): Undef<AvailableTabs> => {
  const {
    isCarrierNetworkQuotingEnabled,
    isLoadBuildingEnabled,
    isTruckListEnabled,
    defaultQuoteViewTab,
  } = features;
  const isQuickQuoteTabEnabled = isQuickQuoteUiEnabled(features);

  // Check if service configured default quote tab is provided and valid
  if (defaultQuoteViewTab) {
    switch (defaultQuoteViewTab) {
      case AvailableTabs.QuickQuote:
        if (isQuickQuoteTabEnabled) return AvailableTabs.QuickQuote;
        break;
      case AvailableTabs.CarrierQuoting:
        if (isCarrierNetworkQuotingEnabled) return AvailableTabs.CarrierQuoting;
        break;
      case AvailableTabs.LoadBuilding:
        if (isLoadBuildingEnabled) return AvailableTabs.LoadBuilding;
        break;
      case AvailableTabs.TrucksList:
        if (isTruckListEnabled) return AvailableTabs.TrucksList;
        break;
    }
  }

  // Fallback to priority order if configured tab is not available
  if (isQuickQuoteTabEnabled) return AvailableTabs.QuickQuote;
  if (isCarrierNetworkQuotingEnabled) return AvailableTabs.CarrierQuoting;
  if (isLoadBuildingEnabled) return AvailableTabs.LoadBuilding;
  if (isTruckListEnabled) return AvailableTabs.TrucksList;

  // No tabs enabled is intentional: customers may be onboarded ~2 weeks before
  // kickoff so we can ingest their emails while views/tabs/features stay off.
  return undefined;
};

/**
 * Set the tab based on the hierarchy of suggestions.
 * Note that this is independent and after setting the *initially displayed* tab based purely on service features.
 * For example, a service has QQ & LB enabled, but no QQ suggestions for the current thread, only LBs
 * In this case, setDefaultTabByFeatureFlagsHierarchy will initially set to QQ tab based on feature flags,
 *  but once LB suggestions are loaded, setTabBySuggestionHierarchy will set the tab to LB as it has higher priority.
 * (This is a bit of a contrived example as it's unlikely a thread has QQ without LB suggestions.)
 *
 * @param features - The service features enabled.
 * @param quoteRequestSuggestions - The quote request suggestions.
 * @param loadBuildingSuggestions - The load building suggestions.
 * @param truckListResult - The truck list suggestions.
 */
const setTabBySuggestionHierarchy = (
  features: ServiceFeaturesListType,
  quoteRequestSuggestions: Maybe<SuggestedQuoteChange[]>,
  loadBuildingSuggestions: Maybe<LoadBuildingSuggestions[]>,
  truckListResult: MaybeUndef<TruckListResponse>
): Undef<AvailableTabs> => {
  const {
    isCarrierNetworkQuotingEnabled,
    isLoadBuildingEnabled,
    isLoadBuildingSuggestionCardsEnabled,
    isTruckListEnabled,
    defaultQuoteViewTab,
  } = features;
  const isQuickQuoteTabEnabled = isQuickQuoteUiEnabled(features);

  // Handled quote requests alone don't pull the rep to the Quick Quote tab.
  if (quoteRequestSuggestions?.some(isQuoteRequestActionable)) {
    // QR suggestions can be applied to both QQ and CQ tabs
    // Prefer configured default tab if present
    if (
      defaultQuoteViewTab === AvailableTabs.CarrierQuoting &&
      isCarrierNetworkQuotingEnabled
    ) {
      return AvailableTabs.CarrierQuoting;
    }
    // Otherwise default to QQ first if enabled
    if (isQuickQuoteTabEnabled) {
      return AvailableTabs.QuickQuote;
    }
    if (isCarrierNetworkQuotingEnabled) {
      return AvailableTabs.CarrierQuoting;
    }
  }

  // Built load building cards alone don't pull the rep to the Load Building tab.
  if (
    loadBuildingSuggestions?.some(isLoadBuildingSuggestionActionable) &&
    isLoadBuildingEnabled &&
    isLoadBuildingSuggestionCardsEnabled
  ) {
    return AvailableTabs.LoadBuilding;
  }

  // Truck List suggestions
  if (isTruckListEnabled && truckListResult) {
    return AvailableTabs.TrucksList;
  }

  return undefined;
};

type QuoteSidebarProps = {
  email: Maybe<Email>;
  // Lets the host load an email that wasn't found because it was still being processed.
  onEmailProcessed?: () => void;
};

// Stable empty lists so effects keyed on the suggestion lists don't re-run on every render.
const NO_QUOTE_SUGGESTIONS: SuggestedQuoteChange[] = [];
const NO_LOAD_BUILDING_SUGGESTIONS: LoadBuildingSuggestions[] = [];

export default function QuoteSidebar({
  email,
  onEmailProcessed,
}: QuoteSidebarProps) {
  const threadID = email?.threadID || null;

  // Per-thread data is stored with the thread it was loaded for, so after a thread switch
  // the previous thread's data reads as empty until the new thread's fetch lands.
  const [isLoadingQuoteSuggestions, setIsLoadingQuoteSuggestions] =
    useState(false);
  const [quoteSuggestions, setQuoteSuggestionsForThread] = useThreadScopedState<
    Maybe<SuggestedQuoteChange[]>
  >(threadID, NO_QUOTE_SUGGESTIONS);

  const [
    isLoadingLoadBuildingSuggestions,
    setIsLoadingLoadBuildingSuggestions,
  ] = useState(false);
  const [loadBuildingSuggestions, setLoadBuildingSuggestionsForThread] =
    useThreadScopedState<Maybe<LoadBuildingSuggestions[]>>(
      threadID,
      NO_LOAD_BUILDING_SUGGESTIONS
    );

  const [allSuggestions, setAllSuggestions] = useState<GenericSuggestion[]>([]);
  const [cloneRecommendations, setCloneRecommendations] = useState<
    LoadCloneRecommendation[]
  >([]);
  const [cloneRecommendationsReload, setCloneRecommendationsReload] =
    useState(0);
  const [suggestionsReload, setSuggestionsReload] = useState(0);
  // Keeps the "still processing latest email" bubble through the post-ingest refetch only.
  // Thread-check refetches bump suggestionsReload without this, so they don't fake that message.
  const [holdProcessingMessageForRefetch, setHoldProcessingMessageForRefetch] =
    useState(false);
  const [cloneRecommendationsError, setCloneRecommendationsError] =
    useState(false);
  const [blockedCloneRecommendations, setBlockedCloneRecommendations] =
    useState<Record<number, string>>({});
  const [selectedCloneRecommendationId, setSelectedCloneRecommendationId] =
    useState<Maybe<number>>(null);
  const [cloneJobStatuses, setCloneJobStatuses] = useState<
    Partial<Record<number, CloneJobStatus>>
  >({});
  const handleCloneJobStatusChanged = useCallback(
    (id: number, status: CloneJobStatus) => {
      setCloneJobStatuses((previous) =>
        previous[id] === status ? previous : { ...previous, [id]: status }
      );
    },
    []
  );
  const selectedCloneRecommendation = cloneRecommendations.find(
    (recommendation) =>
      recommendation.id === selectedCloneRecommendationId &&
      (recommendation.disposition ===
        LoadCloneRecommendationDisposition.Pending ||
        recommendation.disposition ===
          LoadCloneRecommendationDisposition.Started)
  );
  const [truckListResult, setTruckListResultForThread] = useThreadScopedState<
    Maybe<TruckListResponse>
  >(threadID, null);

  const { serviceFeaturesEnabled, carrierQuoteConfig } = useServiceFeatures();
  const isTqlInbox = useIsTqlInbox();
  const { addTab } = useLoadSearch();
  const { setCurrentView } = useContext(SidebarViewContext);

  // Built load building cards: Kit lists them under Built, and the Builder shows them instead of an
  // empty form when the thread has no open card. Either one opens the load in the Loads view.
  const builtLoadBuildingSuggestions = useMemo(
    () => (loadBuildingSuggestions ?? []).filter(isBuiltLoadBuildingSuggestion),
    [loadBuildingSuggestions]
  );
  const openBuiltLoad = (loadIdentifier: string) => {
    addTab(loadIdentifier, 'load');
    setCurrentView(SidebarView.Loads);
  };

  const [quoteRequest, setQuoteRequestForThread] = useThreadScopedState<
    Maybe<QuoteRequest>
  >(threadID, null);
  const [isLoadingQuoteRequest, setIsLoadingQuoteRequest] = useState(false);
  const quoteRequestRequestIdRef = useRef(0);
  const quoteSuggestionsRequestIdRef = useRef(0);
  const portalQuoteSuggestionsRequestIdRef = useRef(0);
  const loadBuildingSuggestionsRequestIdRef = useRef(0);
  const cloneRecommendationsRequestIdRef = useRef(0);
  const cloneRecommendationsInFlightRef = useRef<
    Maybe<{
      key: string;
      response: ReturnType<typeof getLoadCloneRecommendations>;
    }>
  >(null);
  const cloneJobRequestsRef = useRef(
    new Map<
      string,
      Promise<
        ReturnType<typeof getLoadCloneJob> extends Promise<infer T> ? T : never
      >
    >()
  );
  const pollingOwnerRecommendationIdRef = useRef<Maybe<number>>(null);
  const reconciliationRequestsRef = useRef(new Map<number, Promise<boolean>>());
  const cloneRecommendationsRef = useRef(cloneRecommendations);
  cloneRecommendationsRef.current = cloneRecommendations;

  const {
    isTruckListEnabled,
    isLoadBuildingEnabled,
    isLoadCloningEnabled,
    isLoadBuildingSuggestionCardsEnabled,
    isCarrierNetworkQuotingEnabled,
  } = serviceFeaturesEnabled;
  const isQuickQuoteTabEnabled = isQuickQuoteUiEnabled(serviceFeaturesEnabled);
  const loadCloningEnabled = isLoadCloningEnabled;
  const hasLoadBuildingTab =
    isLoadBuildingEnabled ||
    (loadCloningEnabled &&
      cloneRecommendations.some(
        ({ disposition }) =>
          disposition === LoadCloneRecommendationDisposition.Pending ||
          disposition === LoadCloneRecommendationDisposition.Started
      ));
  const shouldFetchLoadBuildingSuggestions =
    isLoadBuildingEnabled && isLoadBuildingSuggestionCardsEnabled;

  const {
    setCurrentState,
    currentState: { clickedSuggestion, tabId, threadId: sidebarThreadID },
  } = useContext(SidebarStateContext);

  const [tab, setTab] = useState<AvailableTabs>(
    setDefaultTabByFeatureFlagsHierarchy(serviceFeaturesEnabled) ||
      AvailableTabs.QuickQuote
  );
  const pollingOwnerRecommendationId =
    tab === AvailableTabs.LoadBuilding && selectedCloneRecommendation?.source
      ? selectedCloneRecommendation.id
      : null;
  pollingOwnerRecommendationIdRef.current = pollingOwnerRecommendationId;

  const [hasUserManuallySelectedTab, setHasUserManuallySelectedTab] =
    useState(false);

  useEffect(() => {
    const tabIsEnabled =
      (tab === AvailableTabs.QuickQuote && isQuickQuoteTabEnabled) ||
      (tab === AvailableTabs.CarrierQuoting &&
        isCarrierNetworkQuotingEnabled) ||
      (tab === AvailableTabs.LoadBuilding && hasLoadBuildingTab) ||
      (tab === AvailableTabs.TrucksList && isTruckListEnabled);
    if (tabIsEnabled) return;

    const fallbackTab = setDefaultTabByFeatureFlagsHierarchy({
      ...serviceFeaturesEnabled,
      isLoadBuildingEnabled: hasLoadBuildingTab,
    });
    if (fallbackTab) setTab(fallbackTab);
  }, [
    hasLoadBuildingTab,
    isCarrierNetworkQuotingEnabled,
    isQuickQuoteTabEnabled,
    isTruckListEnabled,
    serviceFeaturesEnabled,
    tab,
  ]);

  const quickQuoteTabRef = useRef<HTMLButtonElement>(null);
  const trucksListTabRef = useRef<HTMLButtonElement>(null);
  const loadBuildingTabRef = useRef<HTMLButtonElement>(null);
  const carrierQuotingTabRef = useRef<HTMLButtonElement>(null);
  const emailThreadIdRef = useRef<Maybe<string>>(null);
  emailThreadIdRef.current = threadID;
  // Stable listener delegates here so onMessage always calls the latest handler without
  // re-registering on every render (handleChromeMessage closes over tabId/state).
  const handleChromeMessageRef = useRef<
    (message: SidepanelMessage) => Promise<void>
  >(async () => {});

  const tabRefs: Record<
    AvailableTabs,
    React.RefObject<Maybe<HTMLButtonElement>>
  > = {
    [AvailableTabs.QuickQuote]: quickQuoteTabRef,
    [AvailableTabs.TrucksList]: trucksListTabRef,
    [AvailableTabs.LoadBuilding]: loadBuildingTabRef,
    [AvailableTabs.CarrierQuoting]: carrierQuotingTabRef,
  };

  const fetchQuoteSuggestions = async () => {
    if (!threadID) {
      return;
    }

    let isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid) {
      return;
    }

    const requestId = ++quoteSuggestionsRequestIdRef.current;
    setIsLoadingQuoteSuggestions(true);

    // Set a timeout to clear loading state after 1.5s in case one QR or LB endpoint is slower than the other
    const timeoutId = setTimeout(() => {
      isThreadValid = threadID === emailThreadIdRef.current;
      if (
        !isThreadValid ||
        requestId !== quoteSuggestionsRequestIdRef.current
      ) {
        return;
      }

      setIsLoadingQuoteSuggestions(false);
    }, 1500);

    const res = await getQuickQuoteSuggestion(threadID, {
      includeHandled: true,
    });
    isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid || requestId !== quoteSuggestionsRequestIdRef.current) {
      clearTimeout(timeoutId);
      return;
    }

    setQuoteSuggestionsForThread(
      threadID,
      res.isOk() ? filterInvalidSuggestions(res.value) : []
    );
    setIsLoadingQuoteSuggestions(false);
    clearTimeout(timeoutId); // Clear the timeout if fetch completes first
  };

  const fetchLoadBuildingSuggestions = async () => {
    if (!threadID) {
      return;
    }

    let isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid) {
      return;
    }

    const requestId = ++loadBuildingSuggestionsRequestIdRef.current;
    setIsLoadingLoadBuildingSuggestions(true);

    // Set a timeout to clear loading state after 1.5s
    const timeoutId = setTimeout(() => {
      isThreadValid = threadID === emailThreadIdRef.current;
      if (
        !isThreadValid ||
        requestId !== loadBuildingSuggestionsRequestIdRef.current
      ) {
        return;
      }

      setIsLoadingLoadBuildingSuggestions(false);
    }, 1500);

    const res = await getLoadBuildingSuggestion(threadID, {
      includeHandled: true,
    });
    isThreadValid = threadID === emailThreadIdRef.current;
    if (
      !isThreadValid ||
      requestId !== loadBuildingSuggestionsRequestIdRef.current
    ) {
      clearTimeout(timeoutId);
      return;
    }

    setLoadBuildingSuggestionsForThread(
      threadID,
      res.isOk()
        ? filterInvalidSuggestions(normalizeSuggestions(res.value))
        : []
    );
    setIsLoadingLoadBuildingSuggestions(false);
    clearTimeout(timeoutId); // Clear the timeout if fetch completes first
  };

  const fetchTruckList = async () => {
    if (!threadID) {
      return;
    }

    const emailID = email?.id;
    let isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid || !emailID) {
      return;
    }

    const res = await getTruckList(emailID, threadID);
    isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid) {
      return;
    }

    setTruckListResultForThread(threadID, res.isOk() ? res.value : null);
  };

  const fetchCloneRecommendations = async (
    queueIfBusy = false
  ): Promise<boolean> => {
    if (!threadID || !loadCloningEnabled) return false;
    const requestKey = threadID;
    const inFlight = cloneRecommendationsInFlightRef.current;
    if (inFlight?.key === requestKey) {
      if (!queueIfBusy) return false;
      await inFlight.response;
      if (threadID !== emailThreadIdRef.current) return false;
      return fetchCloneRecommendationsRef.current(true);
    }
    const request = {
      key: requestKey,
      response: getLoadCloneRecommendations(threadID),
    };
    cloneRecommendationsInFlightRef.current = request;
    const requestId = ++cloneRecommendationsRequestIdRef.current;
    const result = await request.response;
    if (cloneRecommendationsInFlightRef.current === request) {
      cloneRecommendationsInFlightRef.current = null;
    }
    if (
      threadID !== emailThreadIdRef.current ||
      requestId !== cloneRecommendationsRequestIdRef.current
    ) {
      return false;
    }
    if (result.isOk()) {
      setCloneJobStatuses((previous) => {
        const previousRecommendations = cloneRecommendationsRef.current;
        const validStatuses = Object.entries(previous).filter(([id]) => {
          const recommendationId = Number(id);
          const before = previousRecommendations.find(
            (recommendation) => recommendation.id === recommendationId
          );
          const after = result.value.find(
            (recommendation) => recommendation.id === recommendationId
          );
          return (
            before &&
            after &&
            before.version === after.version &&
            before.latestJobId === after.latestJobId &&
            after.disposition === LoadCloneRecommendationDisposition.Started
          );
        });
        return validStatuses.length === Object.keys(previous).length
          ? previous
          : Object.fromEntries(validStatuses);
      });
      setCloneRecommendations(result.value);
      setCloneRecommendationsError(false);
      return true;
    } else {
      setCloneRecommendationsError(true);
      return false;
    }
  };

  // Reset state when the threadID is missing.
  const clearThreadEmailState = () => {
    setQuoteSuggestionsForThread(null, []);
    setLoadBuildingSuggestionsForThread(null, []);
    setAllSuggestions([]);
    setQuoteRequestForThread(null, null);
    setTruckListResultForThread(null, null);
    setCloneRecommendations([]);
    setBlockedCloneRecommendations({});
    setCloneRecommendationsError(false);
    setIsLoadingQuoteSuggestions(false);
    setIsLoadingLoadBuildingSuggestions(false);
    setIsLoadingQuoteRequest(false);
  };

  const fetchQuoteRequestData = async () => {
    if (!threadID) {
      return;
    }

    let isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid) {
      return;
    }

    const requestId = ++quoteRequestRequestIdRef.current;
    setIsLoadingQuoteRequest(true);
    const res = await getQuoteRequest(threadID);

    isThreadValid = threadID === emailThreadIdRef.current;
    if (!isThreadValid || requestId !== quoteRequestRequestIdRef.current) {
      return;
    }

    setQuoteRequestForThread(threadID, res.isOk() ? res.value : null);
    setIsLoadingQuoteRequest(false);
  };

  const clearThreadEmailStateRef = useRef(clearThreadEmailState);
  clearThreadEmailStateRef.current = clearThreadEmailState;
  const fetchQuoteRequestDataRef = useRef(fetchQuoteRequestData);
  fetchQuoteRequestDataRef.current = fetchQuoteRequestData;
  const fetchQuoteSuggestionsRef = useRef(fetchQuoteSuggestions);
  fetchQuoteSuggestionsRef.current = fetchQuoteSuggestions;
  const fetchLoadBuildingSuggestionsRef = useRef(fetchLoadBuildingSuggestions);
  fetchLoadBuildingSuggestionsRef.current = fetchLoadBuildingSuggestions;
  const fetchTruckListRef = useRef(fetchTruckList);
  fetchTruckListRef.current = fetchTruckList;
  const fetchCloneRecommendationsRef = useRef(fetchCloneRecommendations);
  fetchCloneRecommendationsRef.current = fetchCloneRecommendations;

  const reconcileCloneRecommendation = useCallback(
    async (recommendation: LoadCloneRecommendation): Promise<boolean> => {
      if (recommendation.threadId !== emailThreadIdRef.current) return false;
      const active = cloneRecommendationsRef.current.find(
        (item) => item.id === recommendation.id
      );
      if (
        !active ||
        active.version !== recommendation.version ||
        active.source?.loadId !== recommendation.source?.loadId
      )
        return false;
      const pending = reconciliationRequestsRef.current.get(recommendation.id);
      if (pending) return pending;
      const binding = `${recommendation.threadId}:${recommendation.id}:${recommendation.version}`;
      setBlockedCloneRecommendations((current) => ({
        ...current,
        [recommendation.id]: binding,
      }));
      const request = fetchCloneRecommendationsRef.current(true);
      reconciliationRequestsRef.current.set(recommendation.id, request);
      const refreshed = await request;
      if (reconciliationRequestsRef.current.get(recommendation.id) !== request)
        return false;
      reconciliationRequestsRef.current.delete(recommendation.id);
      if (!refreshed) return false;
      setBlockedCloneRecommendations((current) => {
        if (current[recommendation.id] !== binding) return current;
        const next = { ...current };
        delete next[recommendation.id];
        return next;
      });
      return true;
    },
    []
  );

  useEffect(() => {
    setSelectedCloneRecommendationId(null);
    setCloneJobStatuses({});
    setCloneRecommendations([]);
    cloneJobRequestsRef.current.clear();
    reconciliationRequestsRef.current.clear();
    setBlockedCloneRecommendations({});
  }, [threadID]);

  useEffect(() => {
    if (loadCloningEnabled) return;
    cloneRecommendationsRequestIdRef.current += 1;
    cloneJobRequestsRef.current.clear();
    reconciliationRequestsRef.current.clear();
    setBlockedCloneRecommendations({});
    setCloneRecommendations([]);
    setSelectedCloneRecommendationId(null);
    setCloneJobStatuses({});
    setCloneRecommendationsError(false);
  }, [loadCloningEnabled]);

  useEffect(() => {
    if (!threadID) {
      clearThreadEmailStateRef.current();
      return;
    }

    fetchQuoteRequestDataRef.current();
    fetchCloneRecommendationsRef.current(true);

    return () => {
      cloneRecommendationsRequestIdRef.current += 1;
      setIsLoadingQuoteRequest(false);
    };
  }, [
    cloneRecommendationsReload,
    email,
    loadCloningEnabled,
    suggestionsReload,
    threadID,
  ]);

  useEffect(() => {
    if (!threadID || !loadCloningEnabled) return;

    const requestedThreadId = threadID;
    const jobs = cloneRecommendations
      .filter(
        (recommendation) =>
          recommendation.disposition ===
            LoadCloneRecommendationDisposition.Started &&
          recommendation.latestJobId &&
          recommendation.id !== pollingOwnerRecommendationId
      )
      .map((recommendation) => ({
        id: recommendation.id,
        version: recommendation.version,
        jobId: recommendation.latestJobId as string,
      }));

    if (jobs.length === 0) return;

    jobs.forEach(({ id, version, jobId }) => {
      const key = `${requestedThreadId}:${id}:${version}:${jobId}`;
      if (cloneJobRequestsRef.current.has(key)) return;
      const request = getLoadCloneJob(jobId);
      cloneJobRequestsRef.current.set(key, request);
      void request
        .then((result) => {
          if (
            requestedThreadId !== emailThreadIdRef.current ||
            cloneJobRequestsRef.current.get(key) !== request
          )
            return;
          setCloneJobStatuses((previous) => {
            let next = previous;
            const current = cloneRecommendationsRef.current.find(
              (recommendation) => recommendation.id === id
            );
            if (
              result.isErr() ||
              !current ||
              current.threadId !== requestedThreadId ||
              current.version !== version ||
              current.latestJobId !== jobId ||
              current.id === pollingOwnerRecommendationIdRef.current
            ) {
              return previous;
            }
            if (next[id] !== result.value.status) {
              next = { ...next, [id]: result.value.status };
            }
            return next;
          });
        })
        .catch(() => {})
        .finally(() => {
          if (cloneJobRequestsRef.current.get(key) === request)
            cloneJobRequestsRef.current.delete(key);
        });
    });
  }, [
    cloneRecommendations,
    loadCloningEnabled,
    pollingOwnerRecommendationId,
    threadID,
  ]);

  // Poll only while a recommendation awaits a decision or a started clone job has not
  // finished; idle threads refresh on focus instead.
  const hasActiveCloneWork = cloneRecommendations.some(
    (recommendation) =>
      recommendation.disposition ===
        LoadCloneRecommendationDisposition.Pending ||
      (recommendation.disposition ===
        LoadCloneRecommendationDisposition.Started &&
        !isFinishedCloneJobStatus(cloneJobStatuses[recommendation.id]))
  );

  useEffect(() => {
    if (!threadID || !loadCloningEnabled) return;
    const refresh = () => fetchCloneRecommendationsRef.current(false);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = hasActiveCloneWork
      ? window.setInterval(refresh, 5000)
      : undefined;
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(interval);
    };
  }, [hasActiveCloneWork, loadCloningEnabled, threadID]);

  // Reset suggestion loading state when the email/thread changes.
  useEffect(() => {
    if (!threadID) {
      return;
    }

    return () => {
      setIsLoadingQuoteSuggestions(false);
      setIsLoadingLoadBuildingSuggestions(false);
    };
  }, [email, serviceFeaturesEnabled, threadID]);

  // Key off the thread, not `email` — a 404ed email loading later must not reset manual entry.
  const logicalThreadID = threadID ?? (sidebarThreadID || null);

  useEffect(() => {
    const defaultTab = setDefaultTabByFeatureFlagsHierarchy(
      serviceFeaturesEnabled
    );
    if (defaultTab) {
      setTab(defaultTab);
    }
    setHasUserManuallySelectedTab(false);
    setSuggestionsReload(0);
    setHoldProcessingMessageForRefetch(false);
  }, [logicalThreadID, serviceFeaturesEnabled]);

  // Get suggestions based on email labels & features enabled
  useEffect(() => {
    if (!threadID) {
      return;
    }

    // Don't check most recent email's labels in case there are load building suggestions earlier in the thread
    if (shouldFetchLoadBuildingSuggestions) {
      fetchLoadBuildingSuggestionsRef.current();
    } else {
      setLoadBuildingSuggestionsForThread(threadID, []);
      setIsLoadingLoadBuildingSuggestions(false);
    }

    if (
      isQuickQuoteTabEnabled // TODO: Support CQ, fix glitchy rendering
    ) {
      fetchQuoteSuggestionsRef.current();
    } else {
      setQuoteSuggestionsForThread(threadID, []);
      setIsLoadingQuoteSuggestions(false);
    }

    if (isTruckListEnabled) {
      fetchTruckListRef.current();
    } else {
      setTruckListResultForThread(threadID, null);
    }
  }, [
    email,
    serviceFeaturesEnabled,
    suggestionsReload,
    threadID,
    shouldFetchLoadBuildingSuggestions,
    isQuickQuoteTabEnabled,
    isTruckListEnabled,
    setLoadBuildingSuggestionsForThread,
    setQuoteSuggestionsForThread,
    setTruckListResultForThread,
  ]);

  useEffect(() => {
    if (isLoadingQuoteSuggestions || isLoadingLoadBuildingSuggestions) {
      return;
    }
    setHoldProcessingMessageForRefetch(false);
  }, [isLoadingQuoteSuggestions, isLoadingLoadBuildingSuggestions]);

  // Once all suggestions are fetched, update Kit suggestion list
  useEffect(() => {
    if (isLoadingQuoteSuggestions || isLoadingLoadBuildingSuggestions) return;

    const next = [
      // TODO: Name fields in load building form are buggy if suggestions are applied before tab is set
      // Show load building suggestions and tab first to
      // 1) workaround bug with and 2) create nice UX where first suggestion and first tab match
      ...(loadBuildingSuggestions || []),
      ...(quoteSuggestions || []),
    ];
    setAllSuggestions((prev) =>
      areCarouselSuggestionListsEquivalent(prev ?? [], next) ? prev : next
    );

    // Only override tab based on suggestions if user hasn't manually selected a tab
    if (!hasUserManuallySelectedTab) {
      const defaultTab = setTabBySuggestionHierarchy(
        serviceFeaturesEnabled,
        quoteSuggestions,
        loadBuildingSuggestions,
        truckListResult
      );

      if (defaultTab) {
        setTab(defaultTab);
      }
    }
  }, [
    quoteSuggestions,
    loadBuildingSuggestions,
    isLoadingQuoteSuggestions,
    isLoadingLoadBuildingSuggestions,
    serviceFeaturesEnabled,
    hasUserManuallySelectedTab,
    truckListResult,
  ]);

  useEffect(() => {
    const hasQuoteViewFeatureSuggestions = hasQuoteViewSuggestions(
      serviceFeaturesEnabled,
      quoteSuggestions,
      loadBuildingSuggestions,
      truckListResult,
      quoteRequest
    );
    const hasVisibleCloneRecommendation = cloneRecommendations.some(
      ({ disposition }) =>
        disposition === LoadCloneRecommendationDisposition.Pending ||
        disposition === LoadCloneRecommendationDisposition.Started
    );
    setCurrentState((prev) => ({
      ...prev,
      hasQuoteViewFeatureSuggestions:
        hasQuoteViewFeatureSuggestions || hasVisibleCloneRecommendation,
    }));
  }, [
    serviceFeaturesEnabled,
    quoteSuggestions,
    loadBuildingSuggestions,
    truckListResult,
    quoteRequest,
    cloneRecommendations,
    setCurrentState,
  ]);

  // No email yet → ask the host to load it; otherwise refetch suggestions.
  const handleLatestEmailProcessed = () => {
    if (!threadID) {
      onEmailProcessed?.();
      return;
    }
    setIsLoadingQuoteSuggestions(true);
    setIsLoadingLoadBuildingSuggestions(true);
    setHoldProcessingMessageForRefetch(true);
    setSuggestionsReload((value) => value + 1);
  };

  // A thread check that found something only adds rows; refetch them for the thread it ran on.
  const handleThreadCheckFoundSuggestions = (checkedThreadID: string) => {
    if (checkedThreadID !== emailThreadIdRef.current) {
      return;
    }
    setSuggestionsReload((value) => value + 1);
  };
  const quoteRequestThreadCheck = useKitThreadCheck({
    threadID,
    emailID: email?.id,
    kind: KitThreadCheckKind.QuoteRequest,
    enabled: isKitThreadCheckAvailable(
      KitThreadCheckKind.QuoteRequest,
      serviceFeaturesEnabled,
      isTqlInbox
    ),
    onFoundSuggestions: handleThreadCheckFoundSuggestions,
  });
  const loadBuildingThreadCheck = useKitThreadCheck({
    threadID,
    emailID: email?.id,
    kind: KitThreadCheckKind.LoadBuilding,
    enabled: isKitThreadCheckAvailable(
      KitThreadCheckKind.LoadBuilding,
      serviceFeaturesEnabled,
      isTqlInbox
    ),
    onFoundSuggestions: handleThreadCheckFoundSuggestions,
  });
  const threadChecks = [
    quoteRequestThreadCheck,
    loadBuildingThreadCheck,
  ].filter((check) => check.state.phase !== KitThreadCheckPhase.Disabled);

  const hasThreadSuggestions = Boolean(
    quoteSuggestions?.length || loadBuildingSuggestions?.length
  );
  const processingThreadID = hasThreadSuggestions
    ? null
    : (threadID ?? (sidebarThreadID || null));

  // After processing ends, keep the drum through the suggestion refetch only (not the
  // initial empty-thread fetch — that would flash empty → drum → empty).
  const isProcessingLatestEmail =
    useLatestEmailProcessing(processingThreadID, handleLatestEmailProcessed) ||
    (holdProcessingMessageForRefetch &&
      !hasThreadSuggestions &&
      (isLoadingQuoteSuggestions || isLoadingLoadBuildingSuggestions));

  // Typing before picking a suggestion is a manual entry, so suggestions that land later show
  // as pills and don't switch the tab or fill over it.
  const stopAutoSelectForManualEntry = () => {
    if (clickedSuggestion != null) {
      return;
    }
    setHasUserManuallySelectedTab(true);
    setCurrentState((prev) =>
      prev.clickedSuggestion == null && prev.shouldKitAutoSelect
        ? { ...prev, shouldKitAutoSelect: false }
        : prev
    );
  };

  const handleChromeMessage = async (message: SidepanelMessage) => {
    // Only respond to messages from the tab that this Drumkit sidepanel instance is open on & connected to.
    // e.g. user has Drumkit open on two tabs, two different bidding portals, don't mix messages/data between them.
    if (
      message.targetTabId !== undefined &&
      tabId > 0 &&
      message.targetTabId !== tabId
    ) {
      return;
    }

    const clearClickedSuggestionIfPipeline = (
      pipeline: SuggestionPipelines
    ) => {
      startTransition(() => {
        setCurrentState((prevState) => {
          const shouldClearClicked =
            prevState.clickedSuggestion?.pipeline === pipeline;
          if (!shouldClearClicked) {
            return prevState;
          }

          return {
            ...prevState,
            clickedSuggestion: null,
          };
        });
      });
    };

    const handleUpdateQuoteRequestData = async (
      data: Maybe<CreateQuoteRequestSuggestionRequest>
    ) => {
      const requestId = ++portalQuoteSuggestionsRequestIdRef.current;

      // If data is null, clear the suggestions
      if (data === null) {
        clearClickedSuggestionIfPipeline(SuggestionPipelines.QuickQuote);
        setQuoteSuggestionsForThread(threadID, []);
        setAllSuggestions((suggestions) =>
          suggestions.filter(
            (suggestion) =>
              suggestion.pipeline !== SuggestionPipelines.QuickQuote
          )
        );
        return;
      }

      // A selected portal load supersedes an in-flight thread fetch.
      quoteSuggestionsRequestIdRef.current += 1;
      setIsLoadingQuoteSuggestions(false);

      const requestData = await resolveTransporeonMissingStates(data);
      if (requestId !== portalQuoteSuggestionsRequestIdRef.current) {
        return;
      }

      const sourceCustomerName =
        requestData.portalCustomerName || requestData.customer?.name || '';
      const result = await createQuoteRequestSuggestion(requestData);
      if (requestId !== portalQuoteSuggestionsRequestIdRef.current) {
        return;
      }

      const suggestion = result.isOk()
        ? withSuggestedCustomerName(result.value, sourceCustomerName)
        : buildLocalQuoteSuggestionFromRequest(requestData, sourceCustomerName);

      if (result.isErr()) {
        captureException(new Error('quote suggestion fail-open in sidebar'), {
          action: UpdateQuoteRequestDataAction,
          source: requestData.source,
          sourceExternalID: requestData.sourceExternalID,
          sourceCategory: requestData.sourceCategory,
          message: result.error.message,
        });
      }

      setQuoteSuggestionsForThread(threadID, (prev) => {
        // Portal scrapes re-send the same load as a new object; keep the previous
        // reference when load identity is unchanged (see getCarouselSuggestionStabilityKey).
        const next = [suggestion];
        return prev && areCarouselSuggestionListsEquivalent(prev, next)
          ? prev
          : next;
      });

      startTransition(() => {
        setCurrentState((prevState) => {
          const prev =
            prevState.clickedSuggestion as Undef<SuggestedQuoteChange>;
          // Same stability key as quoteSuggestions / carousel — avoid rebuilding
          // QQ defaults on identical scrape refreshes.
          if (
            prev?.pipeline === SuggestionPipelines.QuickQuote &&
            getCarouselSuggestionStabilityKey(prev) ===
              getCarouselSuggestionStabilityKey(suggestion)
          ) {
            return prevState;
          }

          return {
            ...prevState,
            clickedSuggestion: suggestion,
            shouldKitAutoSelect: true,
            pendingSuggestionNavigation: {
              suggestionID: suggestion.id,
              suggestionPipeline: SuggestionPipelines.QuickQuote,
            },
          };
        });
      });
    };

    const handleUpdateLoadBuildingSuggestionData = async (
      data: Maybe<CreateLoadBuildingSuggestionRequest>
    ) => {
      if (data === null) {
        clearClickedSuggestionIfPipeline(SuggestionPipelines.LoadBuilding);
        setLoadBuildingSuggestionsForThread(threadID, []);
        return;
      }

      const result = await createLoadBuildingSuggestion(data);
      // Prefer BE-resolved entities when available; fall back to portal payload on fail-open.
      const loadBuildingSuggestion =
        buildLocalLoadBuildingSuggestionFromRequest(
          data,
          result.isOk() ? result.value.suggestionID : 0,
          result.isOk() ? result.value.suggested : undefined
        );

      if (result.isErr()) {
        captureException(
          new Error('load-building suggestion fail-open in sidebar'),
          {
            action: UpdateLoadBuildingSuggestionDataAction,
            source: data.source,
            sourceExternalID: data.sourceExternalID,
            sourceCategory: data.sourceCategory,
            message: result.error.message,
          }
        );
      }

      clearClickedSuggestionIfPipeline(SuggestionPipelines.LoadBuilding);
      setLoadBuildingSuggestionsForThread(threadID, [loadBuildingSuggestion]);
      startTransition(() => {
        setCurrentState((prevState) => ({
          ...prevState,
          clickedSuggestion: loadBuildingSuggestion,
          shouldKitAutoSelect: true,
        }));
      });
    };

    if (message.action === UpdateQuoteRequestDataAction) {
      await handleUpdateQuoteRequestData(
        message.data as Maybe<CreateQuoteRequestSuggestionRequest>
      );
      return;
    }

    if (message.action === UpdateLoadBuildingSuggestionDataAction) {
      await handleUpdateLoadBuildingSuggestionData(
        message.data as Maybe<CreateLoadBuildingSuggestionRequest>
      );
    }
  };

  handleChromeMessageRef.current = handleChromeMessage;

  useEffect(() => {
    if (typeof chrome !== 'undefined' && chrome.runtime) {
      // Add listener for messages from the background script
      const onChromeMessage = (message: SidepanelMessage) => {
        void handleChromeMessageRef.current(message);
      };
      chrome.runtime.onMessage.addListener(onChromeMessage);

      // Open a port connection to let background script know quote sidebar has loaded & is ready to start receiving messages
      chrome.runtime.connect({
        name: `${QuoteSidebarPortName}-${tabId}`,
      });

      // Cleanup function to remove the listener
      return () => {
        chrome.runtime.onMessage.removeListener(onChromeMessage);
      };
    }
    return;
  }, [tabId]);

  // Redirect to correct tab after Email AI card is clicked
  useEffect(() => {
    if (!clickedSuggestion) return;
    setSelectedCloneRecommendationId(null);

    switch (clickedSuggestion.pipeline) {
      case SuggestionPipelines.QuickQuote:
        if (isQuickQuoteTabEnabled) {
          setTab(AvailableTabs.QuickQuote);
          setHasUserManuallySelectedTab(true); // Mark as manual selection
          break;
        }
        if (isCarrierNetworkQuotingEnabled) {
          setTab(AvailableTabs.CarrierQuoting);
          setHasUserManuallySelectedTab(true); // Mark as manual selection
          break;
        }
        break;
      case SuggestionPipelines.LoadBuilding:
        setTab(AvailableTabs.LoadBuilding);
        setHasUserManuallySelectedTab(true); // Mark as manual selection
        break;
      default:
        captureException(
          new Error('Invalid suggestion pipeline: ' + clickedSuggestion)
        );
    }
  }, [
    clickedSuggestion,
    isCarrierNetworkQuotingEnabled,
    isQuickQuoteTabEnabled,
  ]);

  const carrerQuoteTabLabel =
    carrierQuoteConfig?.customName !== ''
      ? carrierQuoteConfig?.customName
      : 'Carrier Quote';

  // Kit workaround: carrier quotes use suggestedQuote, not a SuggestionPipelines row
  const preloadedCarrierQuoteCount =
    isCarrierNetworkQuotingEnabled && quoteRequest?.carrierQuotes
      ? quoteRequest.carrierQuotes.filter((cq) => cq.suggestedQuote != null)
          .length
      : 0;
  let loadBuildingContent = null;
  if (selectedCloneRecommendation) {
    loadBuildingContent = (
      <LoadCloneRecommendationCard
        key={selectedCloneRecommendation.id}
        recommendation={selectedCloneRecommendation}
        onRecommendationConflict={() =>
          reconcileCloneRecommendation(selectedCloneRecommendation)
        }
        reconciliationBlocked={Boolean(
          blockedCloneRecommendations[selectedCloneRecommendation.id]
        )}
        onChanged={(updated) => {
          if (
            !loadCloningEnabled ||
            threadID !== emailThreadIdRef.current ||
            (updated && updated.threadId !== threadID)
          )
            return;
          if (updated) {
            cloneRecommendationsRequestIdRef.current += 1;
            setCloneRecommendations((current) =>
              current.map((item) => (item.id === updated.id ? updated : item))
            );
          }
          setCloneRecommendationsReload((value) => value + 1);
        }}
        onOpenLoad={(identifier) => {
          addTab(identifier, 'load');
          setCurrentView(SidebarView.Loads);
        }}
        onUseBuilder={() => setSelectedCloneRecommendationId(null)}
        allowBuilder={isLoadBuildingEnabled}
        onJobStatusChanged={handleCloneJobStatusChanged}
      />
    );
  } else if (isLoadBuildingEnabled) {
    loadBuildingContent = (
      <LoadBuildingTab
        builtSuggestions={builtLoadBuildingSuggestions}
        onOpenBuiltLoad={openBuiltLoad}
      />
    );
  }

  return (
    <div className='flex-1 w-full'>
      <div className='mt-4'>
        <ExcelSharepointLoadBuildingHost>
          <KitEmailSuggestions
            suggestions={allSuggestions}
            currentEmailId={email?.id}
            isProcessingLatestEmail={isProcessingLatestEmail}
            threadFreightTrackingIDs={email?.freightTrackingIDs}
            preloadedCarrierQuoteCount={preloadedCarrierQuoteCount}
            cloneRecommendations={cloneRecommendations}
            cloneRecommendationsError={cloneRecommendationsError}
            selectedCloneRecommendationId={
              tab === AvailableTabs.LoadBuilding
                ? selectedCloneRecommendation?.id
                : null
            }
            cloneJobStatuses={cloneJobStatuses}
            blockedCloneRecommendationIds={Object.keys(
              blockedCloneRecommendations
            ).map(Number)}
            onCloneRecommendationConflict={reconcileCloneRecommendation}
            threadChecks={threadChecks}
            onOpenAssociatedLoad={(identifier) => addTab(identifier, 'load')}
            onOpenBuiltLoad={openBuiltLoad}
            onCloneRecommendationsChanged={(updated) => {
              if (
                !loadCloningEnabled ||
                threadID !== emailThreadIdRef.current ||
                (updated && updated.threadId !== threadID)
              ) {
                return;
              }
              if (updated) {
                cloneRecommendationsRequestIdRef.current += 1;
                setCloneRecommendations((current) =>
                  current.map((item) =>
                    item.id === updated.id ? updated : item
                  )
                );
              }
              setCloneRecommendationsReload((value) => value + 1);
            }}
            onSelectCloneRecommendation={(recommendation) => {
              setSelectedCloneRecommendationId(recommendation.id);
              setTab(AvailableTabs.LoadBuilding);
              setHasUserManuallySelectedTab(true);
              setCurrentState((prev) => ({
                ...prev,
                clickedSuggestion: null,
                batchSelectedSuggestionIds: [],
                shouldKitAutoSelect: false,
              }));
            }}
          />
          <Tabs
            value={tab}
            onValueChange={(nextTab) => {
              const tabValue = nextTab as AvailableTabs;
              setTab(tabValue);
              setHasUserManuallySelectedTab(true);

              if (
                tabValue === AvailableTabs.LoadBuilding &&
                selectedCloneRecommendation
              )
                return;

              let pipeline: Maybe<SuggestionPipelines> = null;
              if (tabValue === AvailableTabs.LoadBuilding) {
                pipeline = SuggestionPipelines.LoadBuilding;
              } else if (tabValue === AvailableTabs.QuickQuote) {
                pipeline = SuggestionPipelines.QuickQuote;
              }

              if (!pipeline) {
                return;
              }

              setCurrentState((prev) => {
                const nextClicked = resolveClickedSuggestionForPipelineTab(
                  prev.curSuggestionList,
                  prev.clickedSuggestion,
                  pipeline
                );

                // If no pending suggestion on this tab: clear selection and disable auto-select
                // so Kit doesn't re-select any other suggestions and end up blocking the tab.
                if (!nextClicked) {
                  if (
                    prev.clickedSuggestion == null &&
                    !prev.shouldKitAutoSelect
                  ) {
                    return prev;
                  }

                  return {
                    ...prev,
                    clickedSuggestion: null,
                    batchSelectedSuggestionIds: [],
                    shouldKitAutoSelect: false,
                  };
                }

                if (
                  nextClicked.id === prev.clickedSuggestion?.id &&
                  prev.shouldKitAutoSelect
                ) {
                  return prev;
                }

                return {
                  ...prev,
                  clickedSuggestion: nextClicked,
                  shouldKitAutoSelect: true,
                };
              });
            }}
            className='w-full h-full flex-1 shrink-0 flex flex-col'
          >
            <TabsList className='w-full overflow-x-auto flex gap-0'>
              {isQuickQuoteTabEnabled && (
                <TabsTrigger
                  value={AvailableTabs.QuickQuote}
                  ref={tabRefs.quickQuote}
                  variant={TabsTriggerVariants.QuoteTabs}
                >
                  <CircleDollarSign
                    className='w-5 h-5 shrink-0'
                    strokeWidth={1}
                  />
                  <Typography
                    className='min-w-0 truncate text-brand-main'
                    variant='body-sm'
                  >
                    Quick Quote
                  </Typography>
                </TabsTrigger>
              )}
              {isCarrierNetworkQuotingEnabled && (
                <TabsTrigger
                  value={AvailableTabs.CarrierQuoting}
                  ref={tabRefs.carrierQuoting}
                  variant={TabsTriggerVariants.QuoteTabs}
                >
                  <Truck className='w-5 h-5 shrink-0' strokeWidth={1} />
                  <Typography
                    className='min-w-0 truncate text-brand-main'
                    variant='body-sm'
                  >
                    {carrerQuoteTabLabel}
                  </Typography>
                </TabsTrigger>
              )}
              {hasLoadBuildingTab && (
                <TabsTrigger
                  value={AvailableTabs.LoadBuilding}
                  ref={tabRefs.loadBuilding}
                  variant={TabsTriggerVariants.QuoteTabs}
                >
                  <HammerIcon className='w-5 h-5 shrink-0' strokeWidth={1} />
                  <Typography
                    className='min-w-0 truncate text-brand-main'
                    variant='body-sm'
                  >
                    Load Building
                  </Typography>
                  <LoadBuildingTabSharepointSpinner />
                </TabsTrigger>
              )}
              {isTruckListEnabled && (
                <TabsTrigger
                  value={AvailableTabs.TrucksList}
                  ref={tabRefs.trucksList}
                  variant={TabsTriggerVariants.QuoteTabs}
                >
                  <ListChecksIcon
                    className='w-5 h-5 shrink-0'
                    strokeWidth={1}
                  />
                  <Typography
                    className='min-w-0 truncate text-brand-main'
                    variant='body-sm'
                  >
                    Truck List
                  </Typography>
                </TabsTrigger>
              )}
            </TabsList>
            <div
              className='px-4 flex-1 shrink-0'
              onInputCapture={stopAutoSelectForManualEntry}
            >
              {isQuickQuoteTabEnabled && (
                <TabsContent value={AvailableTabs.QuickQuote}>
                  <ErrorBoundary>
                    <QuickQuoteTab email={email} />
                  </ErrorBoundary>
                </TabsContent>
              )}
              {isCarrierNetworkQuotingEnabled && (
                <TabsContent value={AvailableTabs.CarrierQuoting}>
                  <ErrorBoundary>
                    <CarrierQuoteTab
                      email={email}
                      quoteRequest={quoteRequest}
                      setQuoteRequest={(next) =>
                        setQuoteRequestForThread(threadID, next)
                      }
                      isLoadingRequest={isLoadingQuoteRequest}
                    />
                  </ErrorBoundary>
                </TabsContent>
              )}
              {hasLoadBuildingTab && (
                <TabsContent value={AvailableTabs.LoadBuilding}>
                  <ErrorBoundary>{loadBuildingContent}</ErrorBoundary>
                </TabsContent>
              )}
              {isTruckListEnabled && (
                <TabsContent value={AvailableTabs.TrucksList}>
                  <ErrorBoundary>
                    <TruckListTab email={email} truckList={truckListResult} />
                  </ErrorBoundary>
                </TabsContent>
              )}
            </div>
          </Tabs>
        </ExcelSharepointLoadBuildingHost>
      </div>
      <Toaster />
    </div>
  );
}
